
import { Component, Mixins } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import Matomo from 'matomo-ts'

import { FiberMapMixin, AuthorizationMixin, SaveScrollMixin, TextToSpeechMixin } from '@/@next/mixins'
import { PointsOfInterestService, CollectionsService, DatasetService, MediasService } from '@/@next/services'
import { SvgIcon, IconTextToSpeech } from '@/@next/components/icons'
import { messages } from './i18n'

import { MAP_URL, MATOMO_URL } from '@/constants'
import { Maybe, PointOfInterest, PointOfInterestList, Collection, Person } from '@/@types/graphql'

import * as UI from '@/event-bus'

import * as AUDIOGUIDE from '@/@next/store/audioguide/types'
import * as BOT from '@/@next/store/bot/types'
import * as SETTINGS from '@/@next/store/settings2/types'

import { CAsyncTask } from '@/@next/components/async-task'
import CustomWrapper from '@/components/CustomWrapper.vue'
import { CImage } from '@/@next/components/image'
import { CLoader } from '@/components/loader'
import { CAudioGuideButton, CAudioGuideCount } from '@/components/audioguide'
import { CTopbarTitle } from '@/@next/components/topbar-title'
import CModal from '@/components/modal'
import VSinglePoiContentPuzzle from '@/views/single-poi/contents/puzzle/Puzzle.vue'
import CAuthorModal from '@/components/modal/author-modal'
import { CPoiContent } from '@/@next/components/poi-content'
import format from 'date-fns/format'
import * as LANGUAGES from '@/@next/store/languages/types'
import { CButton } from '@/components/form-controls/button'

const config = {
  name: 'v-single-poi',
  components: {
    CAsyncTask,
    CPoiContent,
    ...CModal,
    ...CAuthorModal,
    CustomWrapper,
    CAudioGuideButton,
    CAudioGuideCount,
    CLoader,
    CImage,
    VSinglePoiContentPuzzle,
    CTopbarTitle,
    CButton,
    SvgIcon,
    IconTextToSpeech
  },
  i18n: { messages },
  head () {
    return {
      title: this.tabTitle,
      titleTemplate: '%s – Wivi'
    }
  }
}

@Component
class PoiServices extends Mixins(
  PointsOfInterestService,
  CollectionsService,
  DatasetService,
  MediasService
) {}

@Component
class PoiMixins extends Mixins(
  SaveScrollMixin,
  FiberMapMixin,
  AuthorizationMixin,
  TextToSpeechMixin
) {}

@Component(config)
export default class SinglePoi extends Mixins!(PoiServices, PoiMixins) {

  @Getter(AUDIOGUIDE.GET_STATUS) audioguideStatus!: boolean
  @Action(AUDIOGUIDE.SET_STATUS) setAudioguideStatus!: any
  @Action(AUDIOGUIDE.SET_PLAYLIST) setAudioguidePlaylist!: any
  @Action(AUDIOGUIDE.SET_POI) setAudioguidePoi!: any
  @Action(AUDIOGUIDE.SET_CURRENT_POI) setAudioguideCurrentPoi!: (poi: any) => Promise<any>
  @Action(BOT.ADD_POI_CONTENT) addPoiContent!: any
  @Getter(BOT.IS_ENABLE) isBotEnable!: any
  @Getter(BOT.IS_OPEN) isBotOpen!: boolean
  @Action(BOT.CLOSING_BOT) closeBot!: any
  // @Getter(SETTINGS.GET_ALL) getAllSettings!: any
  @Getter(LANGUAGES.GET_CURRENT) getCurrentLanguage!: string
  @Getter(SETTINGS.GET_FEATURES)
  _settingsFeatures!: any
  // @Action(FIBERMAP.CONTENT_HAS_LINK)
  // isPresentOnTheMap!: ({type, uuid}: {type: string, uuid: string}) => boolean
  
  pointOfInterest: Maybe<PointOfInterest> = null
  pointsOfInterest: Maybe<PointOfInterestList> = null
  collection: Maybe<Collection> = null
  showPersonModal: boolean = false
  scrollPosY: number = 0
  mediaScale: number = 1
  person: Person | null = null
  speechSynthesisIsActive: boolean = false
  speechSynthesisIsPlayed: boolean = false
  speechSynthesisIsReady: boolean = false
  mediaSyncContent: any | null = null
  // displayMapLink: boolean = false

  async fetchNextPoi (currentId: PointOfInterest, list: PointOfInterest[]) {
    const index = list.findIndex((p: PointOfInterest) => p.id === currentId)
    const nextId = index < list.length - 1 && list[index + 1]?.id
    if (nextId) {
      await this.GET_SINGLE_POI(nextId)
      this.fetchNextPoi(nextId, list)
    }
  }

  async fetchPreviousPoi (currentId: PointOfInterest, list: PointOfInterest[]) {
    const index = list.findIndex((p: PointOfInterest) => p.id === currentId)
    const prevId = index > 0 && list[index - 1]?.id
    if (prevId) {
      await this.GET_SINGLE_POI(prevId)
      this.fetchPreviousPoi(prevId, list)
    }
  }
  async fetchAroundPointOfInterest (currentId: PointOfInterest, list: PointOfInterest[]) {
    if (list.length < 2) return
    this.fetchNextPoi(currentId, list)
    this.fetchPreviousPoi(currentId, list)
  }

  async getMediaSyncData () {
    try {
      if (!this.pointOfInterest || !this.pointOfInterest.id) return this.mediaSyncContent = null
      const medaiSyncDataset = await this.GET_DATASET('mediaSync')
      if (!medaiSyncDataset || !medaiSyncDataset.value) return this.mediaSyncContent = null
      const mediaSyncData = medaiSyncDataset.value.find((media: any) => {
        // return media.id === 'video29'
        return media.pointOfInterestId === this.pointOfInterest?.id
      })
      if (!mediaSyncData) return
      // Mise en forme des content data
      const { medias, title, description, imageId, ...untranslatedData } = mediaSyncData
      const image = imageId ? await this.GET_SINGLE_IMAGE(imageId) : null 
      const mediaI18n = medias.find((m: any) => m.language === this.$i18n.locale) 
                    || medias.find((m: any) => m.language === 'fr') || []
      const audioId = mediaI18n?.audioId
      const audio = audioId ? await this.GET_SINGLE_AUDIO(audioId) : null 
      if (audio) mediaI18n.audio = audio
      this.mediaSyncContent = {
        __typename: 'MediaSyncContent',
        id: `media-sync-${mediaSyncData.id}`,
        data: {
          __typename: 'MediaSyncContentData',
          ...untranslatedData,
          image,
          i18n: {
            medias: mediaI18n,
            title: title.find((m: any) => m.language === this.$i18n.locale) 
                  || title.find((m: any) => m.language === 'fr')
                  || [],
            description: description.find((m: any) => m.language === this.$i18n.locale) 
                        || description.find((m: any) => m.language === 'fr')
                        || [],
          }
        },
        languages: ['fr'],
        name: 'Media-Sync',
        options: { isAdditionalContent: false },
        people: [],
        shortCord: null,
        slug: null
      }
    } catch (error) {
      console.error(`Error during fetching media Sync data ${error}`)
    }
  }
  
  async init () {
    // On récupère le POI via son identifiant
    this.pointOfInterest = await this.GET_SINGLE_POI(this.$route.params.uuid)
    
    await this.setAudioguideCurrentPoi(this.pointOfInterest)
    await this.getMediaSyncData()

    this.sendMatomoInput()

    // Afin de créer les liens POI suivant et précédent nous devons connaître
    // le contexte lié à ce dernier (une collection, un parcours, aucun)
    if ('collection' in this.$route.query) {
      this.collection = await this.GET_SINGLE_COLLECTION(this.$route.query.collection as string, {
        take: 100
      })
      if (this.collection?.id)
        this.fetchAroundPointOfInterest(this.pointOfInterest.id , this.collection.pointsOfInterest.items)
    }
    if (this.isBotEnableIsCurrentLanguage) this.sendContentToBot()
    // this.displayMapLink = await this.isPresentOnTheMap({type: 'pointOfInterest', uuid: this.pointOfInterest.id})
  }

  get locks () {
    if (!this.pointOfInterest) return []
    return this.pointOfInterest.locks
  }

  get isLocked () {
    return !this.isRessourceAuhtorized(this.locks)
  }

  async activated () {
    await this.setAudioguideCurrentPoi(this.pointOfInterest)
    UI.EventBus.$emit(UI.ENABLE_DEFAULT_TOPBAR_TRANSPARENT)
    this.sendMatomoInput()
  }

  sendMatomoInput () {
    if (!MATOMO_URL || !this.pointOfInterest || !this.pointOfInterest.i18n.title) return
    const title = this.pointOfInterest.i18n.title.value.text as string
    Matomo.default().trackPageView(title)
  }

  deactivated () {
    UI.EventBus.$emit(UI.DISABLE_DEFAULT_TOPBAR_TRANSPARENT)
    this.scrollPosY = 0
    // this.cancelTTS()
    this.resetTts()
  }

  get tabTitle () {
    return (this.pointOfInterest && this.pointOfInterest.i18n.title) ?
      this.pointOfInterest.i18n.title.value.text :
      this.$t('global.loading')
  }

  get pageTitle () {
    return (this.pointOfInterest && this.pointOfInterest.i18n.title) ?
      this.pointOfInterest.i18n.title.value.html :
      this.$t('global.loading')
  }

  get pointsOfInterestAround () {
    if (!this.collection) return null
    const pointsOfInterest = this.collection.pointsOfInterest!.items!
    const currentPoiIndex = pointsOfInterest
      .findIndex((pointOfInterest: PointOfInterest) => pointOfInterest.id === this.pointOfInterest!.id)
    return { prev: pointsOfInterest[currentPoiIndex - 1], next: pointsOfInterest[currentPoiIndex + 1] }
  }

  get prevPointOfInterest () {
    if (this.pointsOfInterestAround)
      return this.pointsOfInterestAround.prev
  }

  get nextPointOfInterest () {
    if (this.pointsOfInterestAround)
      return this.pointsOfInterestAround.next
  }

  get mediaInlineStyles () {
    return {
      'transform': `translateY(-${this.scrollPosY / 5}px)`
    }
  }

  onViewScroll (e: any) {
    const mediaElem: HTMLElement = this.$refs.media as HTMLElement
    if ((mediaElem) && (e.target.scrollTop < mediaElem.offsetHeight)) {
      this.scrollPosY = e.target.scrollTop
      const percentage = (this.scrollPosY * 100) / mediaElem.offsetHeight
      this.mediaScale = 1 + (percentage / 500)
    }
  }

  onStartAudioGuideBtnClick () {
    this.setAudioguidePlaylist([
      ...this.playableAudioGuideTracks
    ]).then(() => {
      this.setAudioguidePoi(this.pointOfInterest)
      this.setAudioguideStatus(true)
    })
  }

  get playableAudioGuideTracks () {
    if (!this.pointOfInterest?.audioGuideTracks.length) return []
    return this.pointOfInterest.audioGuideTracks.filter((t) => t.i18n.audio || t.i18n.video)
  }

  get showAudioGuideButton () {
    if (!this.pointOfInterest) return []
    if (this.isLocked) return
    return this.playableAudioGuideTracks.length
  }

  datesString (bornAt: Date | null, diedAt: Date | null) {
    const formatDate = (d: Date) => format(new Date(d), 'DD/MM/YYYY')
    if (bornAt && diedAt) return `${formatDate(bornAt)} — ${formatDate(diedAt)}`
    if (bornAt && !diedAt) return `${formatDate(bornAt)}`
    if (!bornAt && diedAt) return ` * - ${formatDate(diedAt)}`
    if (!bornAt && !diedAt) return ``
  }

  showPerson (person: Person) {
    this.person = person
    this.showPersonModal = true
  }

  get isBotEnableIsCurrentLanguage () {
    if (this.isBotEnable && this.getCurrentLanguage) {
      const isBotEnable = this.isBotEnable[this.getCurrentLanguage]
      return !!isBotEnable
    }
    return false
  }

  sendContentToBot () {
    if (!this.pointOfInterest) return
    const botContent = this.pointOfInterest.contents.filter((content: any) => content.options.isAdditionalContent)
    if (!botContent || !botContent.length) return
    this.addPoiContent({ ...this.pointOfInterest, contents: botContent })
  }

  get showReadAllPageButton () {
    return this.allowTts && !this.ttsIsStarted
  }

  // initSpeechSynthesis () {
  //   console.log('initSpeechSynthesis in poi')
  //   if (!this._settingsFeatures?.includes('textToSpeech')) return
  //   if (!('speechSynthesis' in window)) return
  //   this.speechSynthesisIsReady = true
  //   // Add Title to speechSynthesis list
  //   // console.log('Coucou', this.pointOfInterest?.i18n.title)
  //   // if (!this.pointOfInterest?.i18n.title) return
  //   // this.addTtsItem({
  //   //   elementId: `title-${this.pointOfInterest.id}`,
  //   //   text: this.pointOfInterest.i18n.title.value.text,
  //   //   locale: this.$i18n.locale
  //   // })
  // }

  getIOSVersion () {
    const agent = window.navigator.userAgent
    const start = agent.indexOf('OS')
    if ((agent.indexOf('iPhone') > -1 || agent.indexOf('iPad') > -1 ) && start > -1) {
      // @ts-ignore
      return window.Number(agent.substr(start + 3, 3).replace('_', '.'))
    }
    return 0
  }

  ClickOnTTSButton () {
    console.log('ClickOnTTSButton')
    this.readTtsList()
    // if (!this.ttsIsPlaying) this.readTtsList()
    // else this.pauseTts()
    // if (!this.speechSynthesisIsActive) this.playSpeechContent()
    // else this.togglePlaySpeechContent()
  }

  // playSpeechContent () {
  //   console.log('playSpeechContent')
  //   const textToSpeechWrapper = (this.$refs.textToSpeech as HTMLElement)
  //   console.log('textToSpeechWrapper', textToSpeechWrapper)
  //   const textToSpeechDomNode: HTMLElement[] =
  //  [].slice.call(textToSpeechWrapper.querySelectorAll('[data-to-speech]'))
  //   console.log(textToSpeechDomNode);
  //   const textToSpeech = textToSpeechDomNode.map((node: HTMLElement) => node.textContent)
  //   console.log(textToSpeech)
  //   window.speechSynthesis.cancel()
  //   if (textToSpeech.length) {
  //     for (const textNode of textToSpeechDomNode ) {
  //       const text = textNode.textContent
  //       console.log('Create utterance with ', text)
  //       const speechSynthesis = new SpeechSynthesisUtterance()
  //       speechSynthesis.lang = this.$i18n.locale
  //       speechSynthesis.volume = 1
  //       speechSynthesis.rate = 1
  //       speechSynthesis.pitch = 1
  //       speechSynthesis.text = text || ''
  //       window.speechSynthesis.speak(speechSynthesis)
  //       this.speechSynthesisIsActive = true
  //       this.speechSynthesisIsPlayed = true
  //       // window.speechSynthesis.resume()
  //       speechSynthesis.onstart = (event) => {
  //           // textNode.style.backgroundColor = '#80808033';
  //           textNode.style.borderRadius = '0.5rem';
  //           textNode.style.border = 'solid 0.2rem var(--primaryColor)'
  //           textNode.style.margin = '-0.5rem';
  //           textNode.style.padding = '0.5rem';
  //           (textNode.parentNode as HTMLElement)?.setAttribute('tts-status', 'playing')
  //           // textNode.style.color = 'var(--onPrimaryColor)';
  //           console.log('started : ', event, text)
  //       };
  //       speechSynthesis.onend = (event) => {
  //         // textNode.style.backgroundColor = 'var(--backgroundColor)';
  //         textNode.style.borderRadius = '0';
  //         textNode.style.border = 'none';
  //         textNode.style.margin = '0';
  //         textNode.style.padding = '0';
  //         (textNode.parentNode as HTMLElement)?.setAttribute('tts-status', 'stop')
  //         // textNode.style.color = 'var(--onBackgroundColor)';
  //         console.log('Finished in ' + event.elapsedTime + ' seconds.', event, text)
  //       };
  //       speechSynthesis.onerror = (event) => {
  //         (textNode.parentNode as HTMLElement)?.setAttribute('tts-status', 'disable')
  //         console.log('Errored ' + event, text)
  //       }
  //       speechSynthesis.onpause = (event) => {
  //         (textNode.parentNode as HTMLElement)?.setAttribute('tts-status', 'paused')
  //         console.log('paused ' + event, text)
  //       }
  //       speechSynthesis.onboundary = (event) => {
  //         console.log('onboundary ', event, text);
  //       }
  //     }
  //       // window.speechSynthesis.resume()
  //   }
  // }

  // togglePlaySpeechContent () {
  //   if (window.speechSynthesis.paused) {
  //     window.speechSynthesis.resume()
  //     this.speechSynthesisIsPlayed = true
  //   } else {
  //     window.speechSynthesis.pause()
  //     this.speechSynthesisIsPlayed = false
  //   }
  // }

  // cancelTTS () {
  //   window.speechSynthesis.cancel()
  //   this.speechSynthesisIsActive = false
  //   this.speechSynthesisIsPlayed = false
  // }
  // goToMap () {
  //   const mapUrl = MAP_URL
  //   const url = 
  // encodeURIComponent(`${mapUrl}?hl=${this.$i18n.locale}&type=pointOfInterest&uuid=${this.pointOfInterest?.id}`)
  //   this.$router.push(`/embeds/?bot=false&topbar=trans&url=${url}`)
  // }
}
